import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Plus, Trash2, Code, ListPlus } from "lucide-react";
import type { ActionConfig, TriggerItem } from "./types";
import { VariablePicker } from "./VariablePicker";

interface WebhookSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerItem?: TriggerItem;
  config: ActionConfig;
  onSave: (config: ActionConfig) => void;
}

interface KeyValueItem {
  key: string;
  value: string;
}

export const WebhookSheet: React.FC<WebhookSheetProps> = ({
  open,
  onOpenChange,
  triggerItem,
  config: initialConfig,
  onSave,
}) => {
  const [url, setUrl] = useState<string>(initialConfig?.url || "");
  const [headers, setHeaders] = useState<KeyValueItem[]>([]);
  const [queryParams, setQueryParams] = useState<KeyValueItem[]>([]);
  const [formatoBody, setFormatoBody] = useState<"json" | "chave_valor">(
    initialConfig?.formato_body || "json",
  );
  const [rawJson, setRawJson] = useState<string>("");
  const [bodyPairs, setBodyPairs] = useState<KeyValueItem[]>([]);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setUrl(initialConfig?.url || "");
    setFormatoBody(initialConfig?.formato_body || "json");

    // Headers
    if (initialConfig?.headers) {
      setHeaders(
        Object.entries(initialConfig.headers).map(([k, v]) => ({
          key: k,
          value: String(v),
        })),
      );
    } else {
      setHeaders([{ key: "Content-Type", value: "application/json" }]);
    }

    // Query Params
    if (initialConfig?.query_params) {
      setQueryParams(
        Object.entries(initialConfig.query_params).map(([k, v]) => ({
          key: k,
          value: String(v),
        })),
      );
    } else {
      setQueryParams([]);
    }

    // Body
    if (initialConfig?.body) {
      setRawJson(JSON.stringify(initialConfig.body, null, 2));
      setBodyPairs(
        Object.entries(initialConfig.body).map(([k, v]) => ({
          key: k,
          value: typeof v === "object" ? JSON.stringify(v) : String(v),
        })),
      );
    } else {
      const defaultBody = {
        evento: "{{item._evento}}",
        registro_id: "{{item.id}}",
        crm_id: "{{item._crm_id}}",
        nome: "{{item.name}}",
      };
      setRawJson(JSON.stringify(defaultBody, null, 2));
      setBodyPairs([
        { key: "evento", value: "{{item._evento}}" },
        { key: "registro_id", value: "{{item.id}}" },
        { key: "crm_id", value: "{{item._crm_id}}" },
      ]);
    }
  }, [initialConfig, open]);

  const handleAddHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleRemoveHeader = (idx: number) => {
    const updated = [...headers];
    updated.splice(idx, 1);
    setHeaders(updated);
  };

  const handleAddQuery = () => {
    setQueryParams([...queryParams, { key: "", value: "" }]);
  };

  const handleRemoveQuery = (idx: number) => {
    const updated = [...queryParams];
    updated.splice(idx, 1);
    setQueryParams(updated);
  };

  const handleAddBodyPair = () => {
    setBodyPairs([...bodyPairs, { key: "", value: "" }]);
  };

  const handleRemoveBodyPair = (idx: number) => {
    const updated = [...bodyPairs];
    updated.splice(idx, 1);
    setBodyPairs(updated);
  };

  const handleInsertTag = (tag: string) => {
    if (formatoBody === "json") {
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? rawJson.length;
        const end = textarea.selectionEnd ?? rawJson.length;
        const next = rawJson.substring(0, start) + tag + rawJson.substring(end);
        setRawJson(next);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + tag.length, start + tag.length);
        }, 0);
      } else {
        setRawJson((prev) => `${prev} ${tag}`);
      }
    } else {
      setBodyPairs([...bodyPairs, { key: "campo", value: tag }]);
    }
  };

  const handleSave = () => {
    const headersObj: Record<string, string> = {};
    for (const h of headers) {
      if (h.key.trim()) headersObj[h.key.trim()] = h.value;
    }

    const queryObj: Record<string, string> = {};
    for (const q of queryParams) {
      if (q.key.trim()) queryObj[q.key.trim()] = q.value;
    }

    let finalBody: Record<string, any> = {};
    if (formatoBody === "json") {
      try {
        finalBody = JSON.parse(rawJson);
      } catch {
        finalBody = { raw: rawJson };
      }
    } else {
      for (const bp of bodyPairs) {
        if (bp.key.trim()) finalBody[bp.key.trim()] = bp.value;
      }
    }

    onSave({
      tipo_acao: "webhook",
      url,
      headers: headersObj,
      query_params: queryObj,
      formato_body: formatoBody,
      body: finalBody,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col h-full overflow-y-auto gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
            <Globe className="w-4 h-4 text-sky-500" />
            Integração Externa
          </div>
          <SheetTitle className="text-xl font-bold">
            Disparar Webhook HTTP POST
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Envia os dados do evento em tempo real para automações no n8n, Make,
            Zapier ou seu próprio backend.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* 1. URL DE DESTINO */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">
              URL do Webhook (Endpoint POST)
            </Label>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1.5 rounded-lg border border-border/80 bg-muted text-[11px] font-mono font-bold text-foreground">
                POST
              </span>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://webhook.site/... ou https://n8n.meudominio.com/..."
                className="text-xs font-mono"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              A requisição é executada em background com timeout de 10s e
              anti-loop ativado.
            </p>
          </div>

          {/* BARRA DE TAGS DE VARIÁVEIS */}
          <VariablePicker
            title="Variáveis Disponíveis para o Payload"
            triggerItem={triggerItem}
            onSelect={handleInsertTag}
          />

          {/* 2. HEADERS HTTP */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">
                Headers HTTP (Opcional)
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddHeader}
                className="h-6 text-[11px] gap-1 text-primary hover:bg-primary/10"
              >
                <Plus className="w-3 h-3" /> Adicionar Header
              </Button>
            </div>

            <div className="space-y-2">
              {headers.map((h, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    placeholder="Chave (ex: Authorization)"
                    value={h.key}
                    onChange={(e) => {
                      const updated = [...headers];
                      updated[idx].key = e.target.value;
                      setHeaders(updated);
                    }}
                    className="h-8 text-xs font-mono w-1/2"
                  />
                  <Input
                    placeholder="Valor (ex: Bearer token...)"
                    value={h.value}
                    onChange={(e) => {
                      const updated = [...headers];
                      updated[idx].value = e.target.value;
                      setHeaders(updated);
                    }}
                    className="h-8 text-xs font-mono w-1/2"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveHeader(idx)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* 2.1 QUERY PARAMS */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">
                Parâmetros de URL / Query (Opcional)
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddQuery}
                className="h-6 text-[11px] gap-1 text-primary hover:bg-primary/10"
              >
                <Plus className="w-3 h-3" /> Adicionar Parâmetro
              </Button>
            </div>

            <div className="space-y-2">
              {queryParams.map((q, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    placeholder="Chave (ex: token)"
                    value={q.key}
                    onChange={(e) => {
                      const updated = [...queryParams];
                      updated[idx].key = e.target.value;
                      setQueryParams(updated);
                    }}
                    className="h-8 text-xs font-mono w-1/2"
                  />
                  <Input
                    placeholder="Valor (ex: 123 ou {{item.id}})"
                    value={q.value}
                    onChange={(e) => {
                      const updated = [...queryParams];
                      updated[idx].value = e.target.value;
                      setQueryParams(updated);
                    }}
                    className="h-8 text-xs font-mono w-1/2"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveQuery(idx)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* 3. FORMATO DO BODY */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">
                Payload / Corpo da Requisição
              </Label>
              <div className="flex items-center rounded-lg border border-border/80 p-0.5 bg-muted/40">
                <button
                  type="button"
                  onClick={() => setFormatoBody("json")}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    formatoBody === "json"
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Code className="w-3 h-3" />
                  JSON Raw
                </button>
                <button
                  type="button"
                  onClick={() => setFormatoBody("chave_valor")}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    formatoBody === "chave_valor"
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <ListPlus className="w-3 h-3" />
                  Chave-Valor
                </button>
              </div>
            </div>

            {formatoBody === "json" ? (
              <div className="space-y-1">
                <Textarea
                  ref={textareaRef}
                  rows={8}
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  placeholder={`{\n  "id": "{{item.id}}",\n  "nome": "{{item.name}}"\n}`}
                  className="font-mono text-xs resize-none bg-muted/30"
                />
                <p className="text-[11px] text-muted-foreground">
                  As tags <code>{"{{item.campo}}"}</code> serão substituídas
                  pelos valores do lead em tempo real.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {bodyPairs.map((bp, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      placeholder="Chave (ex: lead_id)"
                      value={bp.key}
                      onChange={(e) => {
                        const updated = [...bodyPairs];
                        updated[idx].key = e.target.value;
                        setBodyPairs(updated);
                      }}
                      className="h-8 text-xs font-mono w-1/2"
                    />
                    <Input
                      placeholder="Valor ou {{item.campo}}"
                      value={bp.value}
                      onChange={(e) => {
                        const updated = [...bodyPairs];
                        updated[idx].value = e.target.value;
                        setBodyPairs(updated);
                      }}
                      className="h-8 text-xs font-mono w-1/2"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBodyPair(idx)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddBodyPair}
                  className="h-7 text-xs gap-1 border-dashed"
                >
                  <Plus className="w-3 h-3" /> Adicionar Campo
                </Button>
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border/60 flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="gap-1.5 shadow-sm">
            Salvar Webhook
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
